<template>
  <div>
    <TituloPagina :titulo="tituloPagina" data-cy="pagina_dados_bancarios" />
    <v-form>
      <v-row>
        <v-col class="col-12 pb-0">
          <v-autocomplete
            v-model="form.bankNumber"
            :items="bancos"
            item-value="id"
            item-text="description"
            filled
            :filter="customFilterAutocomplete"
            background-color="white"
            label="Nome do banco"
            data-cy="lista_bancos"
            :error-messages="errorMessages('bankNumber')"
            @blur="$v.form.bankNumber.$touch()"
          />
        </v-col>
        <v-col class="col-12 py-0">
          <label>Tipo de conta</label>
          <v-radio-group
            v-model="form.tipoConta"
            row
            class="mt-1 mb-3 fonte-radio"
            hide-details
          >
            <v-radio
              label="Corrente"
              value="1"
              class="pl-0 py-0 mr-10"
              color="grey darken-1"
              data-cy="radio_button_conta_corrente"
            />
            <v-radio
              label="Poupança"
              value="2"
              color="grey darken-1"
              data-cy="radio_button_conta_poupanca"
            />
          </v-radio-group>
        </v-col>
        <v-col
          v-show="form.tipoConta == 2"
          class="col-12 orange darken-1"
          data-cy="card_mensagem_informativa"
        >
          <p class="white--text px-5 ma-0" data-cy="mensagem_informativa">
            Obs: Caso a conta poupança informada esteja vinculada a uma conta corrente,
            a TED do reembolso será efetuada automaticamente na conta corrente.
          </p>
        </v-col>
        <v-col class="col-8 py-0 pr-0 mt-3">
          <v-text-field
            v-model="form.agencyNumber"
            filled
            background-color="white"
            label="Agência"
            type="number"
            min="1"
            data-cy="agencia"
            :error-messages="errorMessages('agencyNumber')"
            @blur="$v.form.agencyNumber.$touch()"
          />
        </v-col>
        <v-col class="col-4 py-0 mt-3">
          <v-text-field
            v-model="form.agencyCheckNumber"
            filled
            background-color="white"
            label="Dígito"
            min="1"
            data-cy="agencia_digito"
            :error-messages="errorMessages('agencyCheckNumber')"
            @blur="$v.form.agencyCheckNumber.$touch()"
          />
        </v-col>
        <v-col class="col-8 py-0 pr-0">
          <v-text-field
            v-model="form.accountNumber"
            v-onlynumbers
            filled
            background-color="white"
            label="Conta"
            type="number"
            min="1"
            data-cy="conta"
            :error-messages="errorMessages('accountNumber')"
            @keypress="$v.form.agencyCheckNumber.$touch()"
            @blur="$v.form.accountNumber.$touch()"
          />
        </v-col>
        <v-col class="col-4 py-0">
          <v-text-field
            v-model="form.accountCheckNumber"
            filled
            background-color="white"
            label="Dígito"
            data-cy="conta_digito"
            :error-messages="errorMessages('accountCheckNumber')"
            @blur="$v.$touch()"
          />
        </v-col>
        <v-col
          v-show="mostrarOperacaoVariacao"
          class="col-12 py-0"
        >
          <v-text-field
            v-model="form.operacaoVariacao"
            filled
            background-color="white"
            label="Operação/Variação"
            type="number"
            min="1"
            :error-messages="errorMessages('operacaoVariacao')"
            @blur="$v.$touch()"
          />
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import TituloPagina from '@/components/TituloPagina.vue';
import { required } from 'vuelidate/lib/validators';
import api from '@/api';
import Moip from '@/utils/moip';


export default {
  name: 'PassoDadosBancarios',

  components: {
    TituloPagina,
  },

  data() {
    return {
      tituloPagina: 'Dados bancários',
      bancos: [],
      form: {
        bankCode: '',
        bankNumber: '',
        bankName: '',
        tipoConta: '1',
        agencyNumber: '',
        agencyCheckNumber: '',
        accountNumber: '',
        accountCheckNumber: '',
        operacaoVariacao: '',
      },
      messages: {
        bankNumber: ['Por favor, preencha o campo banco corretamente.'],
        agencyNumber: ['Por favor, preencha o campo agencia corretamente.'],
        accountNumber: ['Por favor, preencha o campo conta corretamente.'],
        accountCheckNumber: ['Por favor, preencha o campo dígito corretamente.'],
        operacaoVariacao: ['Por favor, preencha o campo operação/variação corretamente.'],
      },
      errorBank: {},
      errors: '',
    };
  },

  computed: {
    mostrarOperacaoVariacao() {
      const BancoDoBrasil = '001';
      const CaixaEconomica = '104';
      const tipoContaPoupanca = '2';

      return this.form.bankNumber === CaixaEconomica
        || (this.form.bankNumber === BancoDoBrasil && this.form.tipoConta === tipoContaPoupanca);
    },
  },

  watch: {
    form: {
      handler() {
        this.validarForm();
      },
      deep: true,
    },
  },

  validations() {
    return {
      form: {
        bankNumber: {
          required,
          invalidBank: Object.keys(this.errorBank).indexOf('bankNumber') !== -1,
        },
        agencyNumber: {
          required,
          invalidAgencyNumber: Object.keys(this.errorBank).indexOf('agencyNumber') !== -1,
        },
        agencyCheckNumber: {
          invalidAgencyCheckNumber: Object.keys(this.errorBank).indexOf('agencyCheckNumber') !== -1,
        },
        accountNumber: {
          required,
          invalidAccountNumber: Object.keys(this.errorBank).indexOf('accountNumber') !== -1,
        },
        accountCheckNumber: {
          required,
          invalidAccountCheckNumber: Object.keys(this.errorBank).indexOf('accountCheckNumber') !== -1,
        },
        operacaoVariacao: {
          required: (value, vm) => !this.mostrarOperacaoVariacao || required(value, vm),
        },
      },
    };
  },

  async created() {
    try {
      const { data } = await api.get('/banks');
      this.bancos = data;
    } catch (error) {
      this.bancos = [];
    }
  },

  methods: {

    customFilterAutocomplete(item, queryText) {
      const description = item.description.toLowerCase();
      const descriptionNormalized = description.normalize('NFD').replace(/[^a-zA-Zs]/g, '');
      const { id } = item;
      const searchText = queryText.toLowerCase();
      const searchTextNormalized = searchText.normalize('NFD').replace(/[^a-zA-Zs]/g, '');

      return description.indexOf(searchText) > -1
        || descriptionNormalized.indexOf(searchTextNormalized) > -1
        || id.indexOf(searchText) > -1;
    },

    validarForm() {
      this.errorBank = {};
      this.validate();

      if (this.form.bankNumber) {
        const banco = this.bancos.find(bank => bank.id === this.form.bankNumber);
        this.form.bankCode = banco.code;
        this.form.bankName = banco.description;
      }

      this.$emit('formValidado', {
        validado: !this.$v.form.$invalid && this.errors == null,
        form: this.form,
      });
    },

    validate() {
      Moip.BankAccount.validate({
        ...this.form,
        valid: () => {
          this.success = true;
          this.errors = null;
        },
        invalid: (data) => {
          this.errors = data.errors;
          this.success = false;
        },
      });
    },

    /* eslint no-else-return: ["error", {allowElseIf: true}] */
    errorMessages(campo) {
      const checkBankValid = this.errorMessagesBank();
      const countErros = Object.keys(checkBankValid).length;
      if (countErros > 0 && this.$v.form[campo].$dirty) {
        return checkBankValid[campo];
      } else if (this.$v.form[campo].$invalid && this.$v.form[campo].$dirty) {
        return this.messages[campo];
      }

      return [];
    },

    errorMessagesBank() {
      if (this.errors) {
        this.errors.forEach((element) => {
          const errorMessage = element.description.replace('conta corrente', 'conta');
          this.errorBank[this.mapError(element.code)] = errorMessage;
        });
      }

      return this.errorBank;
    },

    /* eslint consistent-return: 0 */
    mapError(code) {
      switch (code) {
        case 'INVALID_BANK_NUMBER':
          return 'bankNumber';
        case 'INVALID_AGENCY_NUMBER':
          return 'agencyNumber';
        case 'INVALID_AGENCY_CHECK_NUMBER':
        case 'AGENCY_CHECK_NUMBER_DONT_MATCH':
          return 'agencyCheckNumber';
        case 'INVALID_ACCOUNT_NUMBER':
          return 'accountNumber';
        case 'INVALID_ACCOUNT_CHECK_NUMBER':
        case 'ACCOUNT_CHECK_NUMBER_DONT_MATCH':
          return 'accountCheckNumber';
        default:
          break;
      }
    },
  },
};
</script>
